<template>
  <div>
    <b-overlay
      variant="white"
      spinner-variant="primary"
      blur="0"
      opacity="1"
      rounded="sm"
      :show="loadingPendingTransactions"
    >
      <b-card>
        <b-card-title class="d-flex justify-content-between">
          <h2>Pending Transactions</h2>
        </b-card-title>
        <div
          v-if="!loadingPendingTransactions"
          class="d-flex align-items-center justify-content-between mb-2"
        >
          <BFormCheckbox
            class="mr-2"
            @input="selectAll"
          >
            Select All
          </BFormCheckbox>
          <b-dropdown
            variant="success"
            right
            :disabled="!selectedRows.length || loadingWaive"
          >
            <template #button-content>
              Waive
            </template>
            <b-dropdown-item @click="waiveSelected('waive_with_comp')">
              <feather-icon icon="FileTextIcon" />
              <span>Waive with compensation</span>
            </b-dropdown-item>
            <b-dropdown-item @click="waiveSelected('waive_without_comp')">
              <feather-icon icon="FileTextIcon" />
              <span>Waive without compensation</span>
            </b-dropdown-item>
          </b-dropdown>
        </div>
        <b-table
          ref="refDriverTransactionListTable"
          class="position-relative"
          :items="pendingTransactions"
          responsive
          :fields="tableColumns"
          primary-key="id"
          show-empty
          empty-text="No matching records found"
        >
          <template #cell(empty)="data">
            <BFormCheckbox
              v-if=" canWaive(data.item)"
              v-model="data.item.selected"
              @change="selectRow(data)"
            />
          </template>
          <template #cell(created_at)="data">
            {{ data.item.created_at | formatDate }}
          </template>
          <template #cell(remark_text)="data">
            <component
              :is="data.item.service == 'trips' ? 'b-link': 'div' "
              :to="{ path: `/trips/${data.item.external_reference_id}` }"
              :class="data.item.added_by == 'trips' ? 'linkStyle' : ''"
            >
              {{ data.item.remark_text }}
            </component>
          </template>
          <template #cell(paid)="data">
            {{ data.item.paid? 'Yes' : 'No' }}
          </template>
          <template #cell(waive)="data">
            {{ data.item.waive? 'Yes' : 'No' }}
          </template>
          <template #cell(action)="data">
            <b-dropdown
              v-if=" canWaive(data.item)"
              variant="link"
            >

              <template #button-content>
                <feather-icon
                  icon="MoreVerticalIcon"
                  size="16"
                  class="align-middle text-body"
                />
              </template>
              <b-dropdown-item @click="waivePendingTransaction('waive_with_comp', data.item.id)">
                <feather-icon icon="FileTextIcon" />
                <span class="align-middle ml-50">Waive with compensation</span>
              </b-dropdown-item>

              <b-dropdown-item @click="waivePendingTransaction('waive_without_comp', data.item.id)">
                <feather-icon icon="EditIcon" />
                <span class="align-middle ml-50">Waive without compensation</span>
              </b-dropdown-item>
            </b-dropdown>
          </template>
        </b-table>
        <base-pagination
          v-model="page"
          :total="totalPendingTransactions"
          @new-per-page="perPage = $event"
        />
      </b-card>
    </b-overlay>
  </div>
</template>

<script>
import {
  BCard, BCardTitle, BTable, BOverlay, BLink, BDropdown, BDropdownItem, BButton, BFormCheckbox,
} from 'bootstrap-vue'
import {
  computed, ref, watch,
} from '@vue/composition-api'
import store from '@/store'
import BasePagination from '@/UI/BasePagination.vue'
import { errorNotification } from '@/utils/toast-notifications'

export default {
  components: {
    BOverlay,
    BCard,
    BCardTitle,
    BTable,
    BasePagination,
    BLink,
    BDropdown,
    BDropdownItem,
    BButton,
    BFormCheckbox,
  },
  setup(_, ctx) {
    const refDriverTransactionListTable = ref(null)
    const tableColumns = [
      { label: '', key: 'empty' },
      { key: 'created_at', label: 'Date & Time' },
      { key: 'transaction_type_label', label: 'Account Name' },
      { key: 'credit_amount', label: 'Credit' },
      { key: 'debit_amount', label: 'Debit' },
      { key: 'added_by', label: 'Added By' },
      { key: 'remark_text', label: 'Remark Text' },
      { key: 'paid', label: 'Paid' },
      { key: 'waive', label: 'Waive' },
      { key: 'action', label: 'Action' },
    ]
    const selectedRows = ref([])
    const totalPendingTransactions = ref(0)
    const loadingPendingTransactions = ref(false)
    const pendingTransactions = computed(() => {
      selectedRows.value = []
      const tableData = store.getters['driverView/getDriverPendingTransactions']
      if (tableData) {
        totalPendingTransactions.value = tableData.total
      }
      return tableData?.pending_transactions.map(el => ({
        ...el,
        selected: false,
      }))
    })
    const perPage = ref(10)
    const page = ref(1)
    const refetchPaginated = async () => {
      loadingPendingTransactions.value = true
      await store.dispatch('driverView/fetchDriverPendingTransactions', {
        page: page.value,
        size: perPage.value,
      })
      loadingPendingTransactions.value = false
    }
    watch([perPage, page], () => {
      refetchPaginated()
    })
    const canWaive = row => (row.credit_amount !== 0 && row.external_reference_id && !row.paid && !row.waive)
    const waivePendingTransaction = async (type, pendingTransactionId) => {
      try {
        await ctx.root.$store.dispatch('billing/waivePendingTransactions', {
          waive: true,
          waive_type: type,
          id: pendingTransactionId,
          actor: 'driver',
        })
        ctx.emit('refetch-driver-wallet')
      } catch (error) {
        const errorCode = error?.response?.data?.statusCode
        if (errorCode === 422) {
          const errMessage = error.response.data.error.detail.message
          errorNotification(errMessage)
        } else {
          errorNotification('Something went wrong')
        }
      }
    }
    const selectRow = evt => {
      const item = selectedRows.value.find(el => el.id === evt.item.id)
      if (item) {
        const ix = selectedRows.value.indexOf(item)
        selectedRows.value.splice(ix, 1)
      } else {
        selectedRows.value.push(evt.item)
      }
    }
    const selectAll = value => {
      if (value) {
        selectedRows.value = []
        pendingTransactions.value.forEach(p => {
          const result = canWaive(p)
          if (result) {
            selectedRows.value.push(p)
            // eslint-disable-next-line no-param-reassign
            p.selected = true
          }
        })
      } else {
        pendingTransactions.value.forEach(p => {
          // eslint-disable-next-line no-param-reassign
          p.selected = false
        })
        selectedRows.value = []
      }
    }
    const loadingWaive = ref(false)
    const waiveSelected = async evt => {
      try {
        loadingWaive.value = true
        await ctx.root.$store.dispatch('billing/waiveAllDriverTransactions', {
          waive_type: evt,
          ids: selectedRows.value.map(el => el.id),
        })
        ctx.emit('refetch-driver-wallet')
      } catch (error) {
        const errorCode = error?.response?.data?.statusCode
        if (errorCode === 422) {
          const errMessage = error.response.data.error.detail.message
          errorNotification(errMessage)
        } else {
          errorNotification('Something went wrong')
        }
      } finally {
        loadingWaive.value = false
      }
    }
    return {
      loadingWaive,
      waiveSelected,
      selectedRows,
      selectAll,
      selectRow,
      refDriverTransactionListTable,
      canWaive,
      waivePendingTransaction,
      refetchPaginated,
      tableColumns,
      pendingTransactions,
      loadingPendingTransactions,
      totalPendingTransactions,
      perPage,
      page,
    }
  },
}
</script>

<style scoped>

.linkStyle {
  color: blue;
}
</style>
